// The QCC finding vocabulary — what an inspector can record a problem AGAINST.
//
// A QCC (Quality Control Check) is a post-repair site visit. 1.0 never had a
// checklist of its own: Gordon's inspector app carried a 24-row form (Interior
// 11 trades, Exterior 13, each rated N/A / Fail / Pass with a comment, plus
// three Yes/No sign-offs per section) and posted it back as a JSON blob. The
// inspectors found the form clunky, and across all 92 blobs it collected the
// rows are 86% "N/A": the inspector was made to rate twenty trades to record
// one problem. 2.0 inverts it (docs/inspection/qcc-plan.md §5): the inspector
// records only what is WRONG — a finding — against an item from THIS list, and
// no findings means the job passed. This file is the list. It is the only
// definition: the office tab, the phone, the backfill of the 92 legacy blobs
// and the tests all read it, and a finding row stores the label it was given
// at the time so a later rename here never rewrites history.
//
// Pure data — no imports — so the browser bundle can share it with the server.
//
// The items are Gordon's, kept verbatim in substance (18 months of use, and the
// one attempt to merge the two sections' sign-offs in October 2024 was
// reverted within two weeks: GPS wants Interior and Exterior apart). Two
// changes of shape: his three unnameable "Other 1/2/3" slots per section are
// ONE "Other" here (the note says what), and his three Yes/No sign-offs per
// section are three section-level items phrased as the problem, because a
// "No" to "scope completed in full" IS a finding.
//
// `legacy` lists the blob keys each item absorbs in the backfill
// (services/qccFindings.js). The nine oldest blobs (30 Oct–6 Nov 2024, the
// merge window) carry the sign-offs UNPREFIXED; those map onto the interior
// items, which is where the plan put them.

export const QCC_SECTIONS = [
  { key: "interior", label: "Interior" },
  { key: "exterior", label: "Exterior" },
];

// Stage of job completion at the visit — Gordon's four options, as numbers.
export const QCC_STAGES = [25, 50, 75, 100];

const trade = (section, key, label, legacy) => ({ section, key, label, kind: "trade", legacy });
const check = (section, key, label, legacy) => ({ section, key, label, kind: "section", legacy });

export const QCC_TRADES = [
  trade("interior", "int_carpentry", "Carpentry", ["interiorCarpentry"]),
  trade("interior", "int_gib", "Gib repairs", ["interiorGibRepairs"]),
  trade("interior", "int_decorating", "Decorating", ["interiorDecorating"]),
  trade("interior", "int_plumbing", "Plumbing", ["interiorPlumbing"]),
  trade("interior", "int_electrical", "Electrical / alarms / heat pump", ["interiorElectricalAlarmsHeatpump"]),
  trade("interior", "int_flooring", "Flooring", ["interiorFlooring"]),
  trade("interior", "int_cabinetmaker", "Cabinetmaker / benchtops", ["interiorCabinetmakerBenchtops"]),
  trade("interior", "int_tiling", "Tiling", ["interiorTiling"]),
  trade("interior", "int_other", "Other", ["interiorOther1", "interiorOther2", "interiorOther3"]),
  check("interior", "int_quality", "Work below quality standards / specifications", [
    "interiorWorkMeetsQualityStandardsAndSpecifications",
    "workMeetsQualityStandardsAndSpecifications",
  ]),
  check("interior", "int_scope", "Scope not completed in full", [
    "interiorScopeCompletedInFull",
    "scopeCompletedInFull",
  ]),
  check("interior", "int_tidy", "Repair area not left clean and tidy", [
    "interiorRepairAreaLeftCleanAndTidy",
    "repairAreaLeftCleanAndTidy",
  ]),

  trade("exterior", "ext_carpentry", "Carpentry", ["exteriorCarpentry"]),
  trade("exterior", "ext_plastering", "Plastering", ["exteriorPlastering"]),
  trade("exterior", "ext_painting", "Painting", ["exteriorPainting"]),
  trade("exterior", "ext_plumbing", "Plumbing", ["exteriorPlumbing"]),
  trade("exterior", "ext_roofing", "Roofing", ["exteriorRoofing"]),
  trade("exterior", "ext_spouting", "Spouting / guttering", ["exteriorSpoutingGuttering"]),
  trade("exterior", "ext_brick", "Brick / blockwork", ["exteriorBrickBlockwork"]),
  trade("exterior", "ext_concrete", "Concrete works", ["exteriorConcreteWorks"]),
  trade("exterior", "ext_joinery", "Timber / aluminium joinery", ["exteriorTimberAluminiumJoinery"]),
  trade("exterior", "ext_garage", "Garage doors", ["exteriorGarageDoors"]),
  trade("exterior", "ext_other", "Other", ["exteriorOther1", "exteriorOther2", "exteriorOther3"]),
  check("exterior", "ext_quality", "Work below quality standards / specifications", [
    "exteriorWorkMeetsQualityStandardsAndSpecifications",
  ]),
  check("exterior", "ext_scope", "Scope not completed in full", ["exteriorScopeCompletedInFull"]),
  check("exterior", "ext_tidy", "Repair area not left clean and tidy", ["exteriorRepairAreaLeftCleanAndTidy"]),
];

const BY_KEY = new Map(QCC_TRADES.map((t) => [t.key, t]));

export function tradeByKey(key) {
  return BY_KEY.get(key) || null;
}

export function sectionLabel(key) {
  return QCC_SECTIONS.find((s) => s.key === key)?.label || key;
}

// The items of one section, trades first, then the section-level checks.
export function tradesInSection(section) {
  return QCC_TRADES.filter((t) => t.section === section);
}
